import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { FeatureFlagRulesComponent } from '../feature-flag-rules/feature-flag-rules.component';
import { LaunchDarklyService } from '../../services/launch-darkly.service';
import { FeatureFlag, Rule, EnvironmentState, Member } from '../../models/feature-flag-models';
import { sortEnvironmentsSafely } from '../../utils/environment-order';

@Component({
  selector: 'app-feature-flag-details',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatExpansionModule,
    FeatureFlagRulesComponent
  ],
  template: `
    @if (maintainer) {
      <div class="flag-header">
        <span class="maintainer">
          Maintainer: <a [href]="'mailto:' + maintainer.email">{{ maintainer.firstName }} {{ maintainer.lastName }}</a>
        </span>
      </div>
    }
    
    <mat-tab-group>
      @for (env of environments; track env) {
        <mat-tab [label]="env">
          <div class="tab-content">
            <app-feature-flag-rules
              [rules]="getEnvironmentRules(env)"
              [environment]="env"
              [flagKey]="featureFlag.key"
              [apiKey]="apiKey"
              (rulesChange)="onRulesChange($event, env)"
            ></app-feature-flag-rules>
            <mat-expansion-panel>
              <mat-expansion-panel-header>
                <mat-panel-title>
                  JSON View
                </mat-panel-title>
              </mat-expansion-panel-header>
              <pre>{{ getEnvironmentState(env) | json }}</pre>
            </mat-expansion-panel>
          </div>
        </mat-tab>
      }
    </mat-tab-group>
    `,
  styles: [
    `
      .flag-header {
        padding: 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
      }
      .maintainer {
        color: rgba(0, 0, 0, 0.6);
        font-size: 14px;
      }
      .maintainer a {
        color: #1976d2;
        text-decoration: none;
      }
      .maintainer a:hover {
        text-decoration: underline;
      }
      .tab-content {
        padding: 20px;
      }
      pre {
        white-space: pre-wrap;
        word-wrap: break-word;
      }
    `
  ]
})
export class FeatureFlagDetailsComponent implements OnInit {
  @Input() featureFlag!: FeatureFlag;
  @Input() apiKey = '';

  maintainer: Member | null = null;

  constructor(private readonly launchDarklyService: LaunchDarklyService) {}

  ngOnInit(): void {
    if (!this.featureFlag) {
      throw new Error('FeatureFlag input is required');
    }
    this.loadMaintainerDetails();
  }

  private loadMaintainerDetails(): void {
    if (this.featureFlag.maintainerId) {
      this.launchDarklyService
        .getMember(this.apiKey, this.featureFlag.maintainerId)
        .subscribe(member => {
          this.maintainer = member;
        });
    }
  }

  get environments(): string[] {
    return sortEnvironmentsSafely(Object.keys(this.featureFlag.environments));
  }

  getEnvironmentState(environment: string): EnvironmentState {
    return this.featureFlag.environments[environment];
  }

  getEnvironmentRules(environment: string): Rule[] {
    return this.featureFlag.environments[environment]?.rules || [];
  }

  onRulesChange(rules: Rule[], environment: string): void {
    if (this.featureFlag.environments[environment]) {
      this.featureFlag.environments[environment].rules = rules;
    }
  }
}
